import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { fetchSettings } from '../../../api/settings';
import { addService } from '../../../api/services';
import { addAgentService } from '../../../api/agents/agent-services';
import PillMenu from '../../common/menu/PillMenu';
import ServiceHeader from './ServiceHeader';
import ServiceTools from './ServiceTools';
import ServiceSecrets from './ServiceSecrets';
import ServiceOAuth from './ServiceOAuth';
import ServiceSettings from './ServiceSettings';

import styles from './styles/ServiceDetails.module.css';

const ServiceDetails = ({
    service,
    isAdded,
    isEnabled,
    setServices,
    isAgentContext = false,
    agentId = null,
}) => {
    const [activeTab, setActiveTab] = useState('tools');
    const [enableToolSettings, setEnableToolSettings] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isServiceOwner, setIsServiceOwner] = useState(false);
    const serviceId = useMemo(() => service.id, [service]);

    const loadSettings = useCallback(async () => {
        try {
            setIsLoading(true);
            if (isAgentContext) {
                // For agents we always allow configuring service tools regardless of user settings
                setEnableToolSettings(true);
            } else {
                const data = await fetchSettings();
                setEnableToolSettings(data.services?.enableToolSettings || false);
            }
            setIsServiceOwner(service.isOwner);
        } catch (error) {
            console.error('Error loading settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, [service.isOwner, isAgentContext]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleAdd = useCallback(async () => {
        setIsLoading(true);
        try {
            if (isAgentContext && agentId) {
                await addAgentService(agentId, serviceId);
            } else {
                await addService(serviceId);
            }
            setServices(prev => prev.map(s =>
                s.id === serviceId
                    ? { ...s, isAdded: true, isEnabled: true }
                    : s
            ));
        } catch (error) {
            console.error('Error adding service:', error);
            // Show user-friendly error message for agent context
            if (isAgentContext) {
                alert(error.message);
            }
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, setServices, isAgentContext, agentId]);

    const tabs = useMemo(() => {
        const needsAuth = !!service.requiresAuth
            && !(isAgentContext ? service.hasAgentToken : service.hasUserToken);
        const missingSecretsCount = isAgentContext
            ? (service.missingAgentSecretsCount || 0)
            : (service.missingUserSecretsCount || 0);
        const configurableSecretsCount = service.configurableSecretsCount || 0;
        // Show tab when user can configure secrets (even if none are currently missing),
        // or when the viewer is the service owner.
        const hasSecretsTab = isServiceOwner || configurableSecretsCount > 0;
        const needsSecrets = missingSecretsCount > 0;

        const tabs = [];
        tabs.push({ id: 'tools', label: 'Tools' });

        if (hasSecretsTab) {
            tabs.push({
                id: 'secrets',
                label: 'Secrets',
                indicator: needsSecrets ? {
                    show: true,
                    tooltip: 'Missing secrets',
                } : undefined,
            });
        }
        if (service.requiresAuth) {
            tabs.push({
                id: 'oauth',
                label: 'OAuth',
                indicator: needsAuth ? {
                    show: true,
                    tooltip: 'OAuth required',
                } : undefined,
            });
        }
        if (isServiceOwner) {
            tabs.push({ id: 'settings', label: 'Settings' });
        }
        return tabs;
    }, [service, isServiceOwner, isAgentContext]);

    if (isLoading && !activeTab) {
        return (
            <div className={styles.container}>
                <div className={styles.text}>Loading...</div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <ServiceHeader service={service} isAdded={isAdded} handleAdd={handleAdd} />

            <div className={styles.tabMenuContainer}>
                <PillMenu tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
            </div>

            {activeTab === 'tools' && (
                <ServiceTools
                    serviceId={serviceId}
                    isAdded={isAdded}
                    isEnabled={isEnabled}
                    enableToolSettings={enableToolSettings}
                    isAgentContext={isAgentContext}
                    agentId={agentId}
                />
            )}

            {activeTab === 'secrets' && (
                <ServiceSecrets
                    serviceId={serviceId}
                    isServiceOwner={isServiceOwner}
                    isAgentContext={isAgentContext}
                    agentId={agentId}
                />
            )}

            {activeTab === 'oauth' && service.requiresAuth && (
                <ServiceOAuth
                    serviceId={serviceId}
                    isServiceOwner={isServiceOwner}
                    isAgentContext={isAgentContext}
                    agentId={agentId}
                />
            )}

            {activeTab === 'settings' && isServiceOwner && (
                <ServiceSettings service={service} setServices={setServices} />
            )}
        </div>
    );
};

export default memo(ServiceDetails);
